import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { storeLineSections } from "./lineLayoutEditor.lib";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import LineLayoutEditor from "./LineLayoutEditor";

import type { LineLayoutEditorHandle } from "./LineLayoutEditor";
import type { LineSection } from "./lineLayoutEditor.lib";

import "./LineLayoutModal.scss";

type LineLayoutModalProps = {
    lineGroupUuid: string;
    sections: LineSection[];
    show: boolean;
    onClose: () => void;
    onSaveSuccess: (sections: LineSection[]) => void;
};

const LineLayoutModal = (props: LineLayoutModalProps) => {
    const { lineGroupUuid, sections, show, onClose, onSaveSuccess } = props;
    const editorRef = useRef<LineLayoutEditorHandle | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
        if (editorRef.current?.isDragActive()) {
            event.preventDefault();
            editorRef.current.cancelDrag();
        }
    };

    const handleSave = async () => {
        if (!editorRef.current) {
            return;
        }

        const sectionsToSave = editorRef.current.getSections();

        setIsSaving(true);
        setError("");

        try {
            const updatedSections = await storeLineSections(lineGroupUuid, sectionsToSave);
            onSaveSuccess(updatedSections);
            onClose();
        } catch (err) {
            setError(translate("common.error_save", "An error occurred while saving data"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            show={show}
            animation={false}
            size="xl"
            className="line-layout-modal"
            onHide={onClose}
            onEscapeKeyDown={handleEscapeKeyDown}
        >
            <Modal.Header>
                <Modal.Title>{translate("LineLayoutEditor.edit_line_layout", "Edit line layout")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ErrorComponent className="mt-0" msg={error} type="error" />
                {show && <LineLayoutEditor ref={editorRef} sections={sections} />}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={onClose} disabled={isSaving}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {translate("common.save", "Save")} {isSaving && <i className="fas fa-spinner fa-spin ml-1" />}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default LineLayoutModal;
